// English to Mongolian Cyrillic conversion utility
// Based on the conversion logic similar to krilleer.blogspot.com

const englishToMongolianMap: { [key: string]: string } = {
  // Vowels
  'a': 'а', 'A': 'А',
  'e': 'э', 'E': 'Э',
  'i': 'и', 'I': 'И',
  'o': 'о', 'O': 'О',
  'u': 'у', 'U': 'У',
  'y': 'ы', 'Y': 'Ы',
  
  // Consonants
  'b': 'б', 'B': 'Б',
  'v': 'в', 'V': 'В',
  'g': 'г', 'G': 'Г',
  'd': 'д', 'D': 'Д',
  'j': 'ж', 'J': 'Ж',
  'z': 'з', 'Z': 'З',
  'k': 'к', 'K': 'К',
  'l': 'л', 'L': 'Л',
  'm': 'м', 'M': 'М',
  'n': 'н', 'N': 'Н',
  'p': 'п', 'P': 'П',
  'r': 'р', 'R': 'Р',
  's': 'с', 'S': 'С',
  't': 'т', 'T': 'Т',
  'f': 'ф', 'F': 'Ф',
  'h': 'х', 'H': 'Х',
  'c': 'ц', 'C': 'Ц',
  
  // Multi-character combinations (must come before single characters)
  'ch': 'ч', 'Ch': 'Ч', 'CH': 'Ч',
  'sh': 'ш', 'Sh': 'Ш', 'SH': 'Ш',
  'ts': 'ц', 'Ts': 'Ц', 'TS': 'Ц',
  'kh': 'х', 'Kh': 'Х', 'KH': 'Х',
  'ng': 'нг', 'Ng': 'Нг', 'NG': 'НГ',
  'zh': 'ж', 'Zh': 'Ж', 'ZH': 'Ж',
  'ya': 'я', 'Ya': 'Я', 'YA': 'Я',
  'yu': 'ю', 'Yu': 'Ю', 'YU': 'Ю',
  'ye': 'е', 'Ye': 'Е', 'YE': 'Е',
  'yo': 'ё', 'Yo': 'Ё', 'YO': 'Ё',
  
  // Combinations for when letters are already converted to Mongolian Cyrillic
  'ЦХ': 'Ч', 'цх': 'ч', // When 'c' became 'ц' and 'h' became 'х'
  'ШХ': 'Ш', 'шх': 'ш', // When 's' became 'с' and 'h' became 'х'
  'ТС': 'Ц', 'тс': 'ц', // When 't' became 'т' and 's' became 'с'
  'КХ': 'Х', 'кх': 'х', // When 'k' became 'к' and 'h' became 'х'
  'ЗХ': 'Ж', 'зх': 'ж', // When 'z' became 'з' and 'h' became 'х'
  
  // Special combinations for Mongolian
  'aa': 'аа', 'Aa': 'Аа', 'AA': 'АА',
  'ee': 'ээ', 'Ee': 'Ээ', 'EE': 'ЭЭ',
  'ii': 'ий', 'Ii': 'Ий', 'II': 'ИЙ',
  'oo': 'оо', 'Oo': 'Оо', 'OO': 'ОО',
  'uu': 'уу', 'Uu': 'Уу', 'UU': 'УУ',
  'yy': 'ыы', 'Yy': 'Ыы', 'YY': 'ЫЫ',
  
  // Common Mongolian name patterns
  'ba': 'ба', 'Ba': 'Ба',
  'ga': 'га', 'Ga': 'Га',
  'da': 'да', 'Da': 'Да',
  'ja': 'жа', 'Ja': 'Жа',
  'za': 'за', 'Za': 'За',
  'ka': 'ка', 'Ka': 'Ка',
  'la': 'ла', 'La': 'Ла',
  'ma': 'ма', 'Ma': 'Ма',
  'na': 'на', 'Na': 'На',
  'pa': 'па', 'Pa': 'Па',
  'ra': 'ра', 'Ra': 'Ра',
  'sa': 'са', 'Sa': 'Са',
  'ta': 'та', 'Ta': 'Та',
  'fa': 'фа', 'Fa': 'Фа',
  'ha': 'ха', 'Ha': 'Ха',
  'ca': 'ца', 'Ca': 'Ца',
  
  // Numbers remain the same
  '0': '0', '1': '1', '2': '2', '3': '3', '4': '4',
  '5': '5', '6': '6', '7': '7', '8': '8', '9': '9'
};

/**
 * Converts English text to Mongolian Cyrillic with immediate character conversion
 * @param text - The English text to convert
 * @returns The converted Mongolian Cyrillic text
 */
export const convertToMongolian = (text: string): string => {
  if (!text) return text;
  
  console.log('🔥 convertToMongolian input:', text);
  
  let result = text;
  
  // First handle multi-character combinations (longer patterns first)
  // Order is important - longer patterns must come before shorter ones
  const multiCharPatterns = [
    'ch', 'sh', 'ts', 'kh', 'ng', 'zh', 'ya', 'yu', 'ye', 'yo',
    'aa', 'ee', 'ii', 'oo', 'uu', 'yy',
    'ba', 'ga', 'da', 'ja', 'za', 'ka', 'la', 'ma', 'na', 'pa', 'ra', 'sa', 'ta', 'fa', 'ha', 'ca'
  ];
  
  for (const pattern of multiCharPatterns) {
    // Use case-insensitive regex for English patterns
    const regex = new RegExp(pattern, 'gi');
    result = result.replace(regex, (match) => {
      // Find the exact match in our map (case-sensitive)
      const exactMatch = englishToMongolianMap[match];
      if (exactMatch) {
        return exactMatch;
      }
      
      // If no exact match, try to find a case-insensitive match
      const lowerMatch = match.toLowerCase();
      const upperMatch = match.toUpperCase();
      
      if (englishToMongolianMap[lowerMatch]) {
        return englishToMongolianMap[lowerMatch];
      }
      if (englishToMongolianMap[upperMatch]) {
        return englishToMongolianMap[upperMatch];
      }
      
      // If still no match, return the original
      return match;
    });
  }
  
  // Then handle single characters
  for (const [english, mongolian] of Object.entries(englishToMongolianMap)) {
    if (english.length === 1) {
      result = result.replace(new RegExp(english, 'g'), mongolian);
    }
  }
  
  // Finally, handle the problematic combinations that arise from character-by-character input
  const problematicCombinations = {
    'ЦХ': 'Ч', 'цх': 'ч',
    'ШХ': 'Ш', 'шх': 'ш', 
    'ТС': 'Ц', 'тс': 'ц',
    'КХ': 'Х', 'кх': 'х',
    'ЗХ': 'Ж', 'зх': 'ж'
  };
  
  for (const [combination, replacement] of Object.entries(problematicCombinations)) {
    if (result.includes(combination)) {
      result = result.replace(new RegExp(combination, 'g'), replacement);
    }
  }
  
  console.log('🔥 convertToMongolian output:', result);
  return result;
};

/**
 * Converts English text to Mongolian Cyrillic with immediate character-by-character conversion
 * This is optimized for real-time typing where users see immediate feedback
 * @param text - The English text to convert
 * @returns The converted Mongolian Cyrillic text
 */
export const convertToMongolianImmediate = (text: string): string => {
  if (!text) return text;
  
  let result = '';
  
  // Process each character immediately
  for (let i = 0; i < text.length; i++) {
    const char = text[i];
    const nextChar = text[i + 1];
    
    // Check for two-character combinations first
    if (nextChar) {
      const twoChar = char.toLowerCase() + nextChar.toLowerCase();
      const twoCharUpper = char.toUpperCase() + nextChar.toUpperCase();
      const twoCharMixed = char + nextChar;
      
      if (englishToMongolianMap[twoChar]) {
        result += englishToMongolianMap[twoChar];
        i++; // Skip the next character since we've processed it
        continue;
      } else if (englishToMongolianMap[twoCharUpper]) {
        result += englishToMongolianMap[twoCharUpper];
        i++; // Skip the next character since we've processed it
        continue;
      } else if (englishToMongolianMap[twoCharMixed]) {
        result += englishToMongolianMap[twoCharMixed];
        i++; // Skip the next character since we've processed it
        continue;
      }
    }
    
    // If no two-character combination, process single character
    const converted = englishToMongolianMap[char] || char;
    result += converted;
  }
  
  return result;
};

/**
 * Checks if a string contains English letters that can be converted to Mongolian
 * @param text - The text to check
 * @returns True if the text contains convertible English letters
 */
export const hasConvertibleEnglishLetters = (text: string): boolean => {
  if (!text) return false;
  
  // Check for English letters (excluding numbers)
  const englishLetterRegex = /[a-zA-Z]/;
  return englishLetterRegex.test(text);
};

/**
 * Converts registration number format specifically for Mongolian registration numbers
 * @param text - The registration number text
 * @returns The converted registration number
 */
export const convertRegistrationNumber = (text: string): string => {
  if (!text) return text;
  
  // First, convert English letters to Mongolian Cyrillic
  let result = convertToMongolian(text);
  
  // Now, apply specific corrections for Mongolian registration number patterns
  // that might arise from character-by-character conversion
  const registrationCombinations: { [key: string]: string } = {
    // Correct combinations that might result from individual character conversions
    'ЦХ': 'Ч', 'цх': 'ч', // If 'c' became 'ц' and 'h' became 'х', then 'цх' should be 'ч'
    'ШХ': 'Ш', 'шх': 'ш', // Similar for 'sh'
    'ТС': 'Ц', 'тс': 'ц', // Similar for 'ts'
    'КХ': 'Х', 'кх': 'х', // Similar for 'kh'
    'ЗХ': 'Ж', 'зх': 'ж', // Similar for 'zh'
    'НГ': 'НГ', 'нг': 'нг', // Keep 'ng' as is
  };
  
  // Apply these corrections
  for (const [combination, replacement] of Object.entries(registrationCombinations)) {
    result = result.replace(new RegExp(combination, 'g'), replacement);
  }
  
  return result;
}; 